import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Shield, MapPin, Camera, TrendingUp, AlertTriangle, ArrowRight, CheckCircle,
  Users, FileText, Radio, Phone, MessageCircle, ExternalLink, BarChart3,
  BookOpen, Activity, Siren
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LIVE_UPDATES = [
  { text: 'Heavy congestion at Jagat Circle - expect 15 min delay', type: 'warning', time: '5 min ago' },
  { text: 'Diversion near Ring Road due to road repair work', type: 'info', time: '12 min ago' },
  { text: 'Signal restored at Humnabad Cross junction', type: 'success', time: '25 min ago' },
  { text: 'Waterlogging reported near Station Road underpass', type: 'warning', time: '40 min ago' },
  { text: 'Traffic smooth on University Road - all signals operational', type: 'success', time: '1 hr ago' },
  { text: 'Police checkpoint active at Aland Road entry', type: 'info', time: '1.5 hrs ago' },
];

export default function HomePage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const statCards = [
    { label: 'Total Reports', value: stats?.total_reports || 0, icon: FileText, color: 'text-primary' },
    { label: 'Violations', value: stats?.violations || 0, icon: AlertTriangle, color: 'text-accent' },
    { label: 'Blackspots', value: stats?.blackspots || 0, icon: MapPin, color: 'text-[hsl(var(--warning))]' },
    { label: 'Resolved', value: stats?.resolved || 0, icon: CheckCircle, color: 'text-[hsl(var(--success))]' },
  ];

  const steps = [
    { num: '01', title: 'Capture Evidence', desc: 'Take a photo or video directly from your device camera at the location.', icon: Camera },
    { num: '02', title: 'Pin Location', desc: 'GPS automatically detects your location. Confirm on the map.', icon: MapPin },
    { num: '03', title: 'Submit Report', desc: 'Choose violation type, add details, and submit securely.', icon: FileText },
    { num: '04', title: 'Track Progress', desc: 'Get updates as traffic police review and resolve your report.', icon: TrendingUp },
  ];

  return (
    <div data-testid="home-page">
      {/* Emergency Alert Banner */}
      <div className="bg-accent text-accent-foreground" data-testid="emergency-banner">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Siren className="w-4 h-4 animate-pulse" />
            <span className="hidden sm:inline">Emergency?</span>
            <span>Call Traffic Helpline:</span>
            <a href="tel:103" className="font-bold underline">103</a>
            <span className="hidden md:inline">| Ambulance:</span>
            <a href="tel:108" className="font-bold underline hidden md:inline">108</a>
          </div>
          <a href="https://wa.me/919876543210?text=Traffic%20Emergency%20in%20Kalaburagi" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs font-bold bg-white/20 px-3 py-1 rounded-full hover:bg-white/30 transition-colors" data-testid="whatsapp-emergency">
            <MessageCircle className="w-3 h-3" /> WhatsApp
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1672084305848-b151e89a8a78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxidXN5JTIwc3RyZWV0JTIwdHJhZmZpYyUyMGluZGlhfGVufDB8fHx8MTc3MzI2MDkzMXww&ixlib=rb-4.1.0&q=85"
            alt="Traffic"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-6 animate-fade-in">
              <Shield className="w-3.5 h-3.5" />
              Kalaburagi District Traffic Safety Initiative
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-tight animate-fade-in-up">
              Report Traffic
              <span className="text-primary block">Violations Instantly</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-lg animate-fade-in-up delay-100">
              Help make Kalaburagi roads safer. Capture evidence, tag locations, and submit reports directly to the traffic police department.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 animate-fade-in-up delay-200">
              {user ? (
                <>
                  <Link to="/report/violation">
                    <Button size="lg" className="gap-2 font-bold uppercase tracking-wide" data-testid="hero-report-violation">
                      <AlertTriangle className="w-4 h-4" />Report Violation
                    </Button>
                  </Link>
                  <Link to="/map">
                    <Button size="lg" variant="outline" className="gap-2" data-testid="hero-view-map">
                      <MapPin className="w-4 h-4" />View Live Map
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button size="lg" className="gap-2 font-bold uppercase tracking-wide" data-testid="hero-get-started">
                      Get Started <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/map">
                    <Button size="lg" variant="outline" className="gap-2" data-testid="hero-explore-map">
                      <MapPin className="w-4 h-4" />Explore Map
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 -mt-8 relative z-20" data-testid="stats-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {statCards.map((s, i) => (
            <Card key={s.label} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{s.label}</p>
                    <p className="text-2xl md:text-3xl font-bold mt-1">{s.value}</p>
                  </div>
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Live Traffic Updates */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="live-updates-section">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Radio className="w-5 h-5 text-accent animate-pulse" />
            <h2 className="text-xl md:text-2xl font-bold tracking-tight">Live Traffic Updates</h2>
            <Badge variant="outline" className="gap-1 text-xs">
              <Activity className="w-3 h-3 text-green-500" /> Live
            </Badge>
          </div>
          <Link to="/map">
            <Button variant="ghost" size="sm" className="gap-1 text-xs" data-testid="view-all-updates">
              View Map <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {LIVE_UPDATES.map((update, i) => (
            <div
              key={i}
              className={`p-4 rounded-xl border flex items-start gap-3 animate-fade-in-up ${
                update.type === 'warning' ? 'border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5' :
                update.type === 'success' ? 'border-green-500/30 bg-green-500/5' :
                'border-blue-500/30 bg-blue-500/5'
              }`}
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                update.type === 'warning' ? 'bg-[hsl(var(--warning))]' :
                update.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
              }`} />
              <div>
                <p className="text-sm font-medium">{update.text}</p>
                <p className="text-xs text-muted-foreground mt-1">{update.time}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="how-it-works">
        <div className="text-left mb-12">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">How It Works</h2>
          <p className="mt-2 text-base text-muted-foreground max-w-lg">Four simple steps to report and help improve road safety in Kalaburagi.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div key={step.num} className="group animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="p-6 rounded-xl border bg-card hover:shadow-lg transition-shadow duration-200 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-extrabold text-primary/20">{step.num}</span>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Links */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-12" data-testid="quick-links-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/dashboard" data-testid="link-dashboard">
            <Card className="hover:shadow-lg transition-shadow h-full group cursor-pointer">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-base mb-1">Traffic Dashboard</h3>
                  <p className="text-sm text-muted-foreground">Real-time stats, peak hours, and violation data</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/blog" data-testid="link-blog">
            <Card className="hover:shadow-lg transition-shadow h-full group cursor-pointer">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--warning))]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[hsl(var(--warning))]/20 transition-colors">
                  <BookOpen className="w-6 h-6 text-[hsl(var(--warning))]" />
                </div>
                <div>
                  <h3 className="font-bold text-base mb-1">Safety Articles</h3>
                  <p className="text-sm text-muted-foreground">Traffic rules, fine list, and safety tips</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/leaderboard" data-testid="link-leaderboard">
            <Card className="hover:shadow-lg transition-shadow h-full group cursor-pointer">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--success))]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[hsl(var(--success))]/20 transition-colors">
                  <Users className="w-6 h-6 text-[hsl(var(--success))]" />
                </div>
                <div>
                  <h3 className="font-bold text-base mb-1">Community Leaderboard</h3>
                  <p className="text-sm text-muted-foreground">Top contributors making roads safer</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Trust & Authority Section */}
      <section className="border-t bg-muted/30" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="text-left mb-10">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Official Traffic Safety Initiative</h2>
            <p className="mt-2 text-base text-muted-foreground max-w-lg">Working with Kalaburagi District Traffic Police to improve road safety</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <Shield className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-bold text-base mb-2">Traffic Police Department</h3>
                <p className="text-sm text-muted-foreground mb-4">Kalaburagi District Traffic Police, Karnataka</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href="tel:103" className="hover:text-primary">Traffic Helpline: 103</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href="tel:08472-255100" className="hover:text-primary">Office: 08472-255100</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">SP Office, Kalaburagi - 585101</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <MessageCircle className="w-8 h-8 text-green-600 mb-4" />
                <h3 className="font-bold text-base mb-2">Message from Officers</h3>
                <p className="text-sm text-muted-foreground italic mb-4">
                  "Every citizen report helps us identify problem areas and take immediate action. Together, we can make Kalaburagi's roads safer for everyone."
                </p>
                <p className="text-xs font-medium text-muted-foreground">- Kalaburagi Traffic Police</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <ExternalLink className="w-8 h-8 text-[hsl(var(--warning))] mb-4" />
                <h3 className="font-bold text-base mb-2">Connect With Us</h3>
                <div className="space-y-3 mt-4">
                  <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 transition-colors text-sm font-medium" data-testid="social-whatsapp">
                    <MessageCircle className="w-5 h-5 text-green-600" /> WhatsApp Channel
                  </a>
                  <a href="https://t.me/kalaburagitraffic" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 transition-colors text-sm font-medium" data-testid="social-telegram">
                    <Radio className="w-5 h-5 text-blue-500" /> Telegram Alerts
                  </a>
                  <a href="https://instagram.com/kalaburagitraffic" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 rounded-lg bg-pink-500/10 hover:bg-pink-500/20 transition-colors text-sm font-medium" data-testid="social-instagram">
                    <Camera className="w-5 h-5 text-pink-500" /> Instagram Updates
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="cta-section">
        <div className="rounded-xl bg-primary p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground">Ready to Make a Difference?</h2>
            <p className="mt-2 text-primary-foreground/80 text-base max-w-md">
              Join fellow citizens in building a safer Kalaburagi. Every report counts.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to={user ? "/report/violation" : "/login"}>
              <Button size="lg" variant="secondary" className="font-bold gap-2" data-testid="cta-report-btn">
                <Camera className="w-4 h-4" />Report Now
              </Button>
            </Link>
            <Link to="/leaderboard">
              <Button size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 gap-2" data-testid="cta-leaderboard-btn">
                <Users className="w-4 h-4" />Leaderboard
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
